import React, { useEffect, useState } from 'react';
import { describeAction, describeType, describeOrigin, NO_CHANGE_EXPLANATION } from './auditGlossary';

/**
 * The audit trail, one row per recorded event, newest first.
 *
 * ⚠️ EVERY VALUE HERE IS UNTRUSTED CONTENT. Comment bodies, task subjects, a
 * connector's own tool arguments, and on a failed login an attacker-chosen
 * username all arrive in these cells. React escapes them on render, which is
 * why nothing in this file builds HTML by hand - and why the CSV export, which
 * leaves that protection, does its own neutering.
 *
 * ⚠️ A ROW IS NEVER HIDDEN. `noOp` rows - where a write path recorded a value
 * that did not change - are MARKED, not filtered. Nothing is dropped from an
 * audit trail (invariant 5); the filter bar lets a reader set them aside, and
 * that is a choice they make and can see.
 */

export const isAccessRow = event => typeof event.action === 'string' && event.action.startsWith('access_');

/** A value as it should read in a cell: the label when there is one, else the raw value. */
export function displayValue(value, label) {
    if (label) return label;
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

const timeOf = timestamp => new Date(timestamp).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
});

function FieldDiff({ change }) {
    return (
        <div className="grid grid-cols-[10rem_1fr_1rem_1fr] gap-2 items-baseline py-1 text-sm">
            <span className="font-mono text-gray-600">{change.field}</span>
            <span className="text-gray-500 line-through decoration-gray-300">
                {displayValue(change.before, change.beforeLabel)}
            </span>
            <span className="text-gray-400">→</span>
            <span className="text-gray-900">
                {displayValue(change.after, change.afterLabel)}
                {/* Both forms are shown: a label is for reading, the stored
                    value is what the record actually says (D6). */}
                {change.afterLabel && (
                    <span className="ml-2 text-xs text-gray-400 font-mono">
                        ({displayValue(change.before)} → {displayValue(change.after)})
                    </span>
                )}
            </span>
        </div>
    );
}

function ExpandedDetail({ event }) {
    const access = isAccessRow(event);

    return (
        // gray-100, not gray-50: with the rows banded white/gray-50, an
        // expanded panel in gray-50 would merge into whichever band it opened
        // under and stop reading as a nested detail.
        <tr className="bg-gray-100">
            <td colSpan={7} className="px-4 py-3 border-b border-gray-200">
                {access ? (
                    <div className="text-sm text-gray-700">
                        <div>
                            <span className="text-gray-500">Route read:</span>{' '}
                            <span className="font-mono">{event.entityId || '—'}</span>
                        </div>
                        <div>
                            <span className="text-gray-500">Outcome:</span>{' '}
                            {event.metadata?.outcome || '—'}
                        </div>
                    </div>
                ) : event.fields?.length ? (
                    <div>
                        {event.fields.map(change => (
                            <FieldDiff key={change.field} change={change} />
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">No field-level changes were recorded on this event.</p>
                )}

                <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-500">
                    <div>
                        <dt className="inline">Actor id: </dt>
                        <dd className="inline font-mono">{event.actorId || '—'}</dd>
                    </div>
                    <div>
                        <dt className="inline">Login: </dt>
                        <dd className="inline font-mono">{event.actorLogin || '—'}</dd>
                    </div>
                    <div>
                        <dt className="inline">Entity id: </dt>
                        <dd className="inline font-mono">{event.entityId || '—'}</dd>
                    </div>
                    <div>
                        <dt className="inline">Written by: </dt>
                        <dd className="inline font-mono">{event.source || '—'}</dd>
                    </div>
                    <div className="col-span-2">
                        <dt className="inline">Audit id: </dt>
                        <dd className="inline font-mono">{event.auditId}</dd>
                    </div>
                </dl>

                {Object.keys(event.metadata || {}).length > 0 && (
                    <details className="mt-2">
                        <summary className="text-xs text-gray-500 cursor-pointer">Metadata</summary>
                        <pre className="mt-1 text-xs bg-white border border-gray-200 rounded p-2 overflow-x-auto">
                            {JSON.stringify(event.metadata, null, 2)}
                        </pre>
                    </details>
                )}
            </td>
        </tr>
    );
}

/**
 * The portal's hover popup style, matched exactly - same classes, same arrow,
 * same shadow as the blocked-task popup in TaskDetailsModal and the Gantt ones
 * in gantt/GanttPopups.js. Do not restyle this in isolation: it is one style
 * used everywhere, and the audit page is not an exception to it.
 */
function HoverPopup({ hover }) {
    if (!hover) return null;

    return (
        <div
            className="fixed z-[80] px-3 py-2 text-sm bg-gray-800 text-white rounded-lg max-w-xs pointer-events-none"
            style={{
                left: hover.x,
                top: hover.y,
                transform: 'translate(-50%, -100%)',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)'
            }}
        >
            {hover.title && <div className="font-bold">{hover.title}</div>}
            <div className={hover.title ? 'mt-1' : ''}>{hover.text}</div>
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800" />
        </div>
    );
}

/**
 * Which rows are open, across reloads - the portal's UI-state rule.
 *
 * Stale ids are harmless: an audit id that is not in the current window simply
 * matches nothing. They are not pruned, so switching scope and switching back
 * finds the same rows still open.
 */
const EXPANDED_STORAGE_KEY = 'adminAuditExpandedRows';

function readExpanded() {
    try {
        const raw = localStorage.getItem(EXPANDED_STORAGE_KEY);
        const ids = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(ids) ? ids : []);
    } catch {
        return new Set();
    }
}

function AuditTable({ events, onSelectEntity, onSelectActor }) {
    const [expanded, setExpanded] = useState(readExpanded);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify([...expanded]));
    }, [expanded]);

    /** Anchored to the top-centre of whatever is hovered, like the Gantt popups. */
    const show = (event, title, text) => {
        const box = event.currentTarget.getBoundingClientRect();
        setHover({ x: box.left + box.width / 2, y: box.top - 8, title, text });
    };
    const hide = () => setHover(null);

    const toggle = auditId => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(auditId)) next.delete(auditId);
        else next.add(auditId);
        return next;
    });

    if (!events.length) {
        return (
            <p className="px-4 py-8 text-center text-gray-500">
                No recorded events match these filters.
            </p>
        );
    }

    return (
        <>
        <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-100 text-left text-gray-600">
                <tr>
                    <th className="px-3 py-2 w-8" />
                    <th className="px-3 py-2 whitespace-nowrap">When</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Who</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2">What</th>
                    <th className="px-3 py-2">Origin</th>
                </tr>
            </thead>
            <tbody>
                {events.map((event, index) => {
                    const access = isAccessRow(event);
                    const denied = event.metadata?.outcome === 'denied';
                    const open = expanded.has(event.auditId);

                    // Banded rows. The page sits on gray-50, so an unbanded
                    // table was one flat block and the eye lost its place
                    // half-way across a wide row. A denied row keeps its red
                    // regardless of where it falls in the banding, and hover is
                    // blue because it has to differ from BOTH bands - it used to
                    // be gray-50, which is one of them, so hovering did nothing
                    // visible at all.
                    const band = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                    return (
                        <React.Fragment key={event.auditId}>
                            <tr
                                className={`border-b border-gray-100 hover:bg-blue-50 ${denied ? 'bg-red-50' : band}`}
                            >
                                <td className="px-3 py-2 align-top">
                                    <button
                                        onClick={() => toggle(event.auditId)}
                                        className="text-gray-400 hover:text-gray-700"
                                        aria-label={open ? 'Collapse' : 'Expand'}
                                    >
                                        {open ? '▾' : '▸'}
                                    </button>
                                </td>
                                <td className="px-3 py-2 align-top whitespace-nowrap text-gray-600">
                                    {timeOf(event.timestamp)}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    <span
                                        className={`px-2 py-0.5 rounded text-xs cursor-help ${
                                            denied ? 'bg-red-100 text-red-800'
                                                : access ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-green-100 text-green-800'
                                        }`}
                                        onMouseEnter={e => show(
                                            e,
                                            denied ? 'denied' : access ? 'access' : 'change',
                                            describeType({ access, denied })
                                        )}
                                        onMouseLeave={hide}
                                    >
                                        {denied ? 'denied' : access ? 'access' : 'change'}
                                    </span>
                                    {event.noOp && (
                                        <span
                                            className="ml-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 cursor-help"
                                            onMouseEnter={e => show(e, 'no change', NO_CHANGE_EXPLANATION)}
                                            onMouseLeave={hide}
                                        >
                                            no change
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    <button
                                        onClick={() => onSelectActor(event)}
                                        className="text-primary-700 hover:underline text-left"
                                        title={event.actorId || 'No actor recorded'}
                                    >
                                        {/* Absent means absent - the resolver never invents a name. */}
                                        {event.actorName || event.actorLogin || event.actorId || 'unattributed'}
                                    </button>
                                </td>
                                <td
                                    className="px-3 py-2 align-top font-mono text-xs text-gray-700 cursor-help"
                                    onMouseEnter={e => show(e, event.action, describeAction(event.action))}
                                    onMouseLeave={hide}
                                >
                                    {event.action}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    {event.entity?.name ? (
                                        <button
                                            onClick={() => onSelectEntity(event)}
                                            className="text-primary-700 hover:underline text-left"
                                        >
                                            {event.entity.name}
                                            {event.entity.parentName && (
                                                <span className="text-gray-500"> · {event.entity.parentName}</span>
                                            )}
                                        </button>
                                    ) : (
                                        <span className="text-gray-500 font-mono text-xs">
                                            {event.entityId || '—'}
                                        </span>
                                    )}
                                </td>
                                <td
                                    className="px-3 py-2 align-top text-gray-500 text-xs cursor-help"
                                    onMouseEnter={e => show(e, event.via || 'no origin', describeOrigin(event.via))}
                                    onMouseLeave={hide}
                                >
                                    {event.via || '—'}
                                </td>
                            </tr>
                            {open && <ExpandedDetail event={event} />}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
        <HoverPopup hover={hover} />
        </>
    );
}

export default AuditTable;
